import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import { Button } from '../../../components/common/buttons/Button';
import { MainHead } from '../../../components/common/head/MainHead';
import { Icon } from '../../../components/common/icon/Icon';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useInfiniteUserSellTractorSearchData } from '../../../hooks/api/userSellTractor/queries/useInfiniteUserSellTractorSearchData.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useInfiniteScrollLoadingTarget } from '../../../hooks/scroll/useInfiniteScrollLoadingTarget';
import {
  IOpenMarketProductForDetailCard,
  OpenMarketProductDetailCard,
} from '../../../views/Product/listView/OpenMarketProductDetailCard/OpenMarketProductDetailCard';
import { OpenMarketProductListDetailCardGroup } from '../../../views/Product/listView/OpenMarketProductDetailCard/OpenMarketProductListDetailCardGroup';

export const OpenMarketSearchResultPage = () => {
  const [searchParams] = useSearchParams();
  const [searchKeyword, setSearchKeyword] = useState<string>(' ');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteUserSellTractorSearchData({
    searchParam: {
      q: searchKeyword.trim(),
      limit: 10, // 페이지당 10개
    },
    options: {
      enabled: Boolean(searchKeyword.trim()),
    },
  });

  const { loadingTargetRef, isLoadingTargetRendered } = useInfiniteScrollLoadingTarget({
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    triggeredFnWhenInView: fetchNextPage,
  });

  // Meilisearch 데이터를 OpenMarketProductDetailCard 형태로 변환 (무한 스크롤용)
  const transformedProducts = useMemo(() => {
    if (!data?.pages) return [];

    return data.pages.flatMap((page) =>
      page.hits
        .filter((product) => {
          if (!product || !product.id) return false;

          // 필터링 조건:
          // 1. Left Image(modelImage)가 있어야 함
          const hasImage = Boolean(product.modelImage);

          // 2. 마력(engineOutputInPs) 또는 조식(rowConfig) 중 하나라도 있어야 함
          const hasPowerOrRow = Boolean(
            product.engineOutputInPs ||
              product.machine?.engineOutputInPs ||
              product.rowConfig ||
              product.machine?.rowConfig,
          );

          // 3. 판매자(seller) 정보가 있어야 함
          const hasSeller = Boolean(product.seller?.id);

          return hasImage && hasPowerOrRow && hasSeller;
        })
        .map(
          (product) =>
            ({
              id: product.id || '',
              code: product.code || '',
              updatedAt: product.updatedAt || '',
              views: product.views || 0,
              inquiryCount: product.inquiryCount || 0,
              pokeCount: product.pokeCount || 0,
              images: product.modelImage
                ? [
                    {
                      id: product.id || '',
                      type: 'main',
                      order: 0,
                      imageUrl: product.modelImage,
                    },
                  ]
                : [],
              machine: {
                id: product.machine?.id || '',
                machineType: product.machine?.machineType || 'tractor',
                model: product.machine?.model || '',
                imageMain: product.machine?.imageMain || '',
                brand: {
                  id: product.brand?.id || '',
                  name: product.brand?.name || '',
                  country: '',
                  image: '',
                },
                horsePower: product.engineOutputInPs || product.machine?.engineOutputInPs || 0,
                rowConfig: product.rowConfig || product.machine?.rowConfig || '',
                engineOutputInPs: product.engineOutputInPs || product.machine?.engineOutputInPs || 0,
                series: '',
                releaseYear: 0,
                length: 0,
                width: 0,
                height: 0,
                weight: 0,
                groundClearance: 0,
                drivingSeatType: '',
                engineModel: '',
                engineGeneration: '',
                engineType: '',
                engineOutputInRpm: 0,
                engineOutputInKw: 0,
                approvalStatus: 'APPROVAL',
                tankCapacity: 0,
                fuelType: '',
                displacement: 0,
                clutchType: '',
                steeringType: '',
                brakeType: '',
                shiftMethod: '',
                forwardShiftNumber: '',
                rearShiftNumber: '',
                drivingMethod: '',
                maxSpeed: 0,
                maxForwardSpeed: 0,
                minForwardSpeed: 0,
                maxReverseSpeed: 0,
                minReverseSpeed: 0,
                frontTire: '',
                rearTire: '',
                ptoType: '',
                ptoShiftNumber: '',
                ratedRpm1: '',
                ratedRpm2: '',
                ratedRpm3: '',
                images: [],
                interaxialDistance: 0,
                hydraulicSystem: '',
                forwardHydraulicOutlet: 0,
                rearHydraulicOutlet: 0,
              },
              manufacturedYear: product.manufacturedYear || 0,
              location: product.location || '',
              operationTime: product.operationTime || 0,
              approvalStatus: (product.approvalStatus as 'APPROVAL' | 'REJECT' | 'ONGOING') || 'APPROVAL',
              status: (product.status as 'ONSALE' | 'SOLDOUT') || 'ONSALE',
              approvalDate: product.createdAt || '',
              hopeSellingPrice: product.hopeSellingPrice || 0,
              priceNegotiationYn: product.priceNegotiationYn || false,
              bump: null,
              isNewProduct: false,
              user: {
                id: product.seller?.id || '',
                phoneNumber: product.seller?.phoneNumber || '',
                nickname: product.seller?.nickname || '',
              },
            }) as IOpenMarketProductForDetailCard,
        ),
    );
  }, [data?.pages]);

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        searchInput={{
          searchKeyword,
          setSearchKeyword,
          inputRef,
          isResultPage: true,
          searchInputType: 'openMarket',
        }}
      />
    ),
    fixedHeaderHeight: 48,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
  });

  useEffect(() => {
    const keyword = searchParams.get('keyword');

    if (keyword) {
      setSearchKeyword(keyword);
    } else {
      navigate(pageInfo.openMarketListPage.path, { replace: true });
    }
  }, [searchParams, navigate]);

  if (isLoading) {
    return <AnimateLoading />;
  }

  if (data?.pages && data.pages[0]?.hits.length === 0) {
    return (
      <>
        <Helmet>
          <title>{pageInfo.openMarketSearchResultPage.title}</title>
        </Helmet>
        <EmptyResultContainer>
          <ContentsContainer>
            <Icon name={'BookBox'} width={60} height={60} />
            <Description>{'찾으시는 정보가 없네요.\n브랜드나 모델명으로 다시 검색해보세요!'}</Description>
          </ContentsContainer>
          <ButtonWrapper>
            <Button
              borderType={'square'}
              size={'md'}
              text={{ size: 'md', content: '다시 검색하기' }}
              color={'filledGreyLight'}
              icon={{ position: 'right', size: 'Medium', fileName: 'AngleRight', fill: '#505363' }}
              onClick={() => {
                ampli.pvmClickOpenMarketSearchResultRetry({
                  typed_keyword: searchKeyword.trim(),
                });
                navigate(pageInfo.openMarketSearchPage.path, { replace: true });
              }}
            />
          </ButtonWrapper>
        </EmptyResultContainer>
      </>
    );
  }

  if (transformedProducts && transformedProducts.length !== 0) {
    return (
      <>
        <Helmet>
          <title>{pageInfo.openMarketSearchResultPage.title}</title>
        </Helmet>
        <SearchResultWrapper>
          <SearchResultCardGroup>
            <OpenMarketProductListDetailCardGroup>
              {transformedProducts.map((product, index) => (
                <OpenMarketProductDetailCard
                  key={product.id}
                  product={product}
                  isNewBadgeVisible
                  indexInList={index}
                  searchKeyword={searchKeyword.trim()}
                />
              ))}
              {isLoadingTargetRendered && <LoadingTarget ref={loadingTargetRef} />}
            </OpenMarketProductListDetailCardGroup>
          </SearchResultCardGroup>
        </SearchResultWrapper>
      </>
    );
  }

  return null;
};

const SearchResultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const SearchResultCardGroup = styled.div`
  display: flex;
  padding: 8px 16px 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const EmptyResultContainer = styled.div`
  width: 100%;
  height: calc(100vh - 48px);

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;

  padding: 16px 16px 32px 16px;
`;

const ContentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  text-align: center;

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const ButtonWrapper = styled.div`
  width: 167px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  margin-top: 8px;
  padding-bottom: 40px;
`;

const LoadingTarget = styled.div``;
